import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@clerk/react';
import {
  type TerminalEventPayload,
  WebSocketEntityType,
} from '@this+that/models';
import log from 'loglevel';

import {
  ConnectionState,
  useWebSocket,
  WebSocketEventType,
} from '../../../context/WebSocket/WebSocketContext';
import { useCustomToast } from '../../../hooks/useCustomToast';
import { useTerminalRequests } from '../../../services/terminal.service';
import { useTerminalStore } from '../../../store/Terminal/useTerminalStore';
import {
  closeTerminalConnection,
  ensureTerminalConnection,
  type TerminalErrorHandler,
} from '../transport/terminalConnection';
import { createTerminalGateway } from '../transport/terminalGateway';
import { readPreferredTerminalDeviceId } from '../utils/terminalDevicePreference';
import {
  classifyDaemonError,
  TerminalDaemonErrorKind,
} from '../utils/terminalErrors';

export function useTerminalConnection(): void {
  const { t } = useTranslation();
  const { user } = useUser();
  const userIdRef = useRef(user?.id);
  userIdRef.current = user?.id;
  const { errorMessage, warningMessage } = useCustomToast();
  const requests = useTerminalRequests();
  const { subscribe, connectionState } = useWebSocket();
  const reportRef = useRef<(code: string) => void>(() => undefined);
  const requestsRef = useRef(requests);
  const subscribeRef = useRef(subscribe);
  const stateListeners = useRef(new Set<(isConnected: boolean) => void>());
  const isConnectedRef = useRef(connectionState === ConnectionState.CONNECTED);

  reportRef.current = (code) => {
    const kind = classifyDaemonError(code);
    if (kind === TerminalDaemonErrorKind.FOLDER_PICK) {
      useTerminalStore.getState().setPickingFolder(false);
      warningMessage(t(`terminal.folders.errors.${code}`));
    } else if (kind === TerminalDaemonErrorKind.ATTACHMENT) {
      errorMessage(t('terminal.attachments.rejected'));
    } else {
      errorMessage(t('terminal.errors.generic'));
    }
  };
  requestsRef.current = requests;
  subscribeRef.current = subscribe;

  useEffect(() => {
    const isConnected = connectionState === ConnectionState.CONNECTED;
    if (isConnectedRef.current === isConnected) {
      return;
    }
    isConnectedRef.current = isConnected;
    for (const listener of [...stateListeners.current]) {
      listener(isConnected);
    }
  }, [connectionState]);

  useEffect(() => {
    const listeners = stateListeners.current;
    const gateway = createTerminalGateway(
      {
        getTerminalDevices: () => requestsRef.current.getTerminalDevices(),
        createTerminalAttachmentUpload: (input) =>
          requestsRef.current.createTerminalAttachmentUpload(input),
        createTerminalTicket: (deviceId) =>
          requestsRef.current.createTerminalTicket(deviceId),
        sendTerminalMessage: (deviceId, message) =>
          requestsRef.current.sendTerminalMessage(deviceId, message),
      },
      {
        subscribe: (listener) =>
          subscribeRef.current<TerminalEventPayload>(
            WebSocketEntityType.TerminalEvent,
            WebSocketEventType.MODIFY,
            ({ item }) => {
              if (item) {
                listener(item);
              }
            },
          ),
        isConnected: () => isConnectedRef.current,
        subscribeState: (listener) => {
          listeners.add(listener);
          return () => {
            listeners.delete(listener);
          };
        },
      },
    );
    const onError: TerminalErrorHandler = (message) => {
      log.error('terminal: daemon error', message.code, message.message);
      reportRef.current(message.code);
    };
    ensureTerminalConnection(gateway, onError, () =>
      readPreferredTerminalDeviceId(userIdRef.current),
    ).catch((error) => {
      log.error('terminal: connection failed', error);
    });
    return closeTerminalConnection;
  }, []);
}
